import { Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { firstValueFrom, Observable } from 'rxjs';
import {
  BuildingItemDto,
  BuildingService,
  EquipmentItemDto,
  EquipmentService,
  TenantDetailDto,
  TenantItemDto,
  TenantService,
} from '@artemis-software/wr-api';
import { currentTenantId, isContractorTenant, isExternalTenant, isInternalTenant } from '@/utils/admin-utils';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { watch } from '@/utils/signals/watch';
import { parseDateString, toDateString } from '@/utils/date-utils';
import { DateRangeObject } from '@models/date-range';
import {
  DEFAULT_FILTER_PARAMS,
  MaintenanceTimerDashboardFilterParams,
} from '@pages/maintenance-timer-dashboard-page/maintenance-timer-dashboard-filter-params';

@Component({
  standalone: false,
  selector: 'wr-maintenance-timer-dashboard-filter',
  templateUrl: './maintenance-timer-dashboard-filter.component.html',
  styleUrls: ['./maintenance-timer-dashboard-filter.component.scss'],
})
export class MaintenanceTimerDashboardFilterComponent {

  private readonly buildingService = inject(BuildingService);
  private readonly equipmentService = inject(EquipmentService);
  private readonly tenantService = inject(TenantService);

  readonly isAdmin = isInternalTenant();
  readonly isExternalTenant = isExternalTenant();
  readonly isContractor = isContractorTenant();
  readonly currentTenantId = currentTenantId();

  readonly contractorCustomers = asyncComputed(() => {
    const tenantId = this.currentTenantId();
    if (this.isContractor() && tenantId) {
      return this.tenantService.findExternalTenantsByContractorId(tenantId);
    }
    return [];
  }, [] as TenantItemDto[]);

  readonly initialParams = input<MaintenanceTimerDashboardFilterParams | undefined>();

  readonly searchRequested = output<MaintenanceTimerDashboardFilterParams>();
  readonly addMaintenanceTimer = output<void>();

  readonly form = new FormGroup({
    tenantId: new FormControl<string | undefined>(undefined),
    customer: new FormControl<TenantItemDto | undefined>(undefined),
    building: new FormControl<BuildingItemDto | undefined>(undefined),
    dateRange: new FormControl<DateRangeObject | undefined>(undefined),
    expired: new FormControl(DEFAULT_FILTER_PARAMS.expired),
    notExpired: new FormControl(DEFAULT_FILTER_PARAMS.notExpired),
    maxDueDays: new FormControl<number | null>(null),
    timerTenantId: new FormControl<string | undefined>(undefined),
    equipment: new FormControl<EquipmentItemDto | undefined>(undefined),
    myTeamOnly: new FormControl(DEFAULT_FILTER_PARAMS.myTeamOnly),
  });

  private readonly selectedTenantId = toSignal(this.form.controls.tenantId.valueChanges, {
    initialValue: this.form.controls.tenantId.value,
  });

  private readonly selectedContractorCustomer = computed(() => {
    const tenantId = this.selectedTenantId();
    return (this.contractorCustomers() ?? []).find(customer => customer.id === tenantId);
  });

  readonly buildingTenantId = computed(() => {
    if (this.isContractor()) {
      return this.selectedTenantId() ?? undefined;
    }
    if (!this.isAdmin()) {
      return this.currentTenantId();
    }
    return this.selectedTenantId() ?? undefined;
  });

  readonly unresolvedLabels = signal<string[]>([]);
  readonly unresolvedHint = computed(
    () =>
      `Aus dem Link konnte Folgendes nicht geladen werden: ${this.unresolvedLabels().join(', ')}. ` +
      'Der Filter bleibt aktiv, bis erneut gesucht wird.',
  );

  private applyVersion = 0;
  private applyingParams = false;

  constructor() {
    this.form.controls.tenantId.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.form.controls.building.setValue(undefined);
    });
    this.form.controls.customer.valueChanges.pipe(takeUntilDestroyed()).subscribe(customer => {
      if (this.form.controls.tenantId.value !== customer?.id) {
        this.form.controls.tenantId.setValue(customer?.id);
      }
    });
    watch(this.selectedContractorCustomer, customer => {
      if (this.form.controls.customer.value?.id !== customer?.id) {
        this.form.controls.customer.setValue(customer, { emitEvent: false });
      }
    });
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.applyingParams) {
        return;
      }
      this.applyVersion++;
      this.unresolvedLabels.set([]);
    });
    watch(this.initialParams, (params) => {
      if (params) {
        void this.applyParams(params);
      }
    });
  }

  currentParams(): MaintenanceTimerDashboardFilterParams {
    const value = this.form.getRawValue();
    return {
      tenantId: value.tenantId ?? undefined,
      buildingId: value.building?.id,
      from: value.dateRange?.start ? toDateString(value.dateRange.start) : undefined,
      to: value.dateRange?.end ? toDateString(value.dateRange.end) : undefined,
      expired: !!value.expired,
      notExpired: !!value.notExpired,
      maxDueDays: value.maxDueDays ?? undefined,
      timerTenantId: value.timerTenantId ?? undefined,
      equipmentId: value.equipment?.id,
      myTeamOnly: !!value.myTeamOnly,
    };
  }

  triggerSearch(): void {
    this.searchRequested.emit(this.currentParams());
  }

  private async applyParams(params: MaintenanceTimerDashboardFilterParams): Promise<void> {
    const version = ++this.applyVersion;
    const controls = this.form.controls;
    this.unresolvedLabels.set([]);

    this.writeWithoutInvalidating(() => {
      setIfChanged(controls.tenantId, params.tenantId);
      setIfChanged(controls.timerTenantId, params.timerTenantId);
      setIfChanged(controls.expired, params.expired);
      setIfChanged(controls.notExpired, params.notExpired);
      setIfChanged(controls.maxDueDays, params.maxDueDays ?? null);
      setIfChanged(controls.myTeamOnly, params.myTeamOnly);

      const dateRange = params.from
        ? { start: parseDateString(params.from), end: params.to ? parseDateString(params.to) : undefined }
        : undefined;
      if (dateRangeKey(controls.dateRange.value) !== dateRangeKey(dateRange)) {
        controls.dateRange.setValue(dateRange);
      }
    });

    const [building, equipment] = await Promise.all([
      this.resolve(params.buildingId, controls.building.value, id => this.buildingService.findByIds([id])),
      this.resolve(params.equipmentId, controls.equipment.value, id => this.equipmentService.findByIds([id])),
    ]);
    // Der Early-Return ueberspringt nur noch die setValue-Writes. Vorher lag er VOR beidem: hat der
    // Nutzer waehrend des laufenden findByIds irgendeine Checkbox angeklickt, blieben Gebaeude- und
    // TGA-Select leer UND der Banner blieb aus, waehrend die Liste weiter nach der URL filterte -
    // der naechste Klick auf "Suchen" las dann das leere Control und weitete die Treffermenge
    // kommentarlos auf. Genau den Zustand soll unresolvedLabels sichtbar machen.
    const superseded = version !== this.applyVersion;
    if (!superseded) {
      this.writeWithoutInvalidating(() => {
        if (controls.building.value?.id !== building?.id) {
          controls.building.setValue(building);
        }
        if (controls.equipment.value?.id !== equipment?.id) {
          controls.equipment.setValue(equipment);
        }
      });
    }

    const unresolved: string[] = [];
    if (params.buildingId && (superseded || !building)) {
      unresolved.push('Gebäude');
    }
    if (params.equipmentId && (superseded || !equipment)) {
      unresolved.push('TGA Bauplan');
    }
    this.unresolvedLabels.set(unresolved);
  }

  private writeWithoutInvalidating(write: () => void): void {
    this.applyingParams = true;
    try {
      write();
    } finally {
      this.applyingParams = false;
    }
  }

  private async resolve<T extends { id?: string }>(
    id: string | undefined,
    current: T | null | undefined,
    fetch: (id: string) => Observable<T[]>,
  ): Promise<T | undefined> {
    if (!id) {
      return undefined;
    }
    if (current?.id === id) {
      return current;
    }
    try {
      const [resolved] = await firstValueFrom(fetch(id));
      return resolved;
    } catch {
      return undefined;
    }
  }

  protected readonly TenantTypeEnum = TenantDetailDto.TypeEnum;
}

function setIfChanged<T>(control: AbstractControl<T>, value: T): void {
  if (control.value !== value) {
    control.setValue(value);
  }
}

function dateRangeKey(range: DateRangeObject | null | undefined): string {
  if (!range?.start) {
    return '';
  }
  return `${toDateString(range.start)}_${range.end ? toDateString(range.end) : ''}`;
}
